import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Typography, IconButton, Tooltip } from '@mui/material';

import TransferList from './TransferList';
import { useEffect } from 'react';
import * as UIActions from '../../redux/actions/ui.actions';
import { setFieldMap, setFieldMapSelection } from '../../redux/actions';
import { connect } from 'react-redux';
import { ChevronLeft, Close as CloseIcon, Check as CheckIcon, InfoOutlined } from '@mui/icons-material';
import store from '../../redux/store'
const isFieldInCurrent = (field, fieldMapItem) => {
  if (!fieldMapItem) return false;
  return fieldMapItem.selected.find((s) => s === field) != undefined;
};

const toFieldMap = (catalog, fieldMapItem, stream, flow = undefined, filter = undefined) => {
  const fieldMap = {
    [stream]: {
      name: stream,
      selected: [],
      notSelected: [],
      isSelected: true,
    },
  };
  let wildcardFlag = false;

  if (fieldMapItem && fieldMapItem[stream]?.selected.includes('*')) {
    fieldMap[stream].selected = ['*'];
    fieldMap[stream].notSelected = [];
    wildcardFlag = true;
  }

  if (fieldMapItem) {
    fieldMap[stream] = { ...fieldMap[stream], ...fieldMapItem };
  }
  const s = catalog.streams.filter((s) => s.stream === stream).shift();
  const name = s.stream;

  s.metadata.forEach((m) => {
    if (m.breadcrumb && m.breadcrumb.length == 0) {
      return;
    }

    const field = m.breadcrumb[1];

    const fieldsToFilter = filter && filter.fieldsFilter && filter.fieldsFilter(flow, stream, field);
    if (fieldsToFilter?.length > 0 && !fieldsToFilter.includes(field)) return fieldMap;

    const isSelected =
      isFieldInCurrent(field, fieldMapItem) || (m.metadata['inclusion'] && m.metadata['inclusion'] == 'automatic');

    if (isSelected && !wildcardFlag) {
      if (!fieldMap[name].selected.find((s) => s === field)) {
        fieldMap[name].selected.push(field);
        fieldMap[name].notSelected = fieldMap[name].notSelected.filter((x) => x !== field);
      }
    } else {
      if (field) {
        if (!fieldMap[name].notSelected.find((s) => s === field))
          fieldMap[name].notSelected.push(field);
      } else {
        console.warn(`WARN: No breadcrumb for the field..${name}:${field}`, m);
      }
    }
  });

  return fieldMap;
};

const FieldMapList = (props) => {
  const { primaryKeys, stream, fieldMapItem, entity, entityId, backToFieldMapSelector, filter, flow, isFlowV2 = false } = props;
  const [allFieldsSelected, setAllFieldsSelected] = useState(fieldMapItem?.selected?.includes('*'));
  const [selected, setSelected] = useState(fieldMapItem?.[stream]?.selected);
  const [notSelected, setNotSelected] = useState(fieldMapItem?.[stream]?.notSelected);
  const [isSaving, setIsSaving] = useState(false);

  const toggleImportAll = () => {
    if (allFieldsSelected) {
      // Turn off all field selection
      setSelected([...primaryKeys]);
      setNotSelected(notSelected.filter((notSelectedField) => !primaryKeys.includes(notSelectedField)));
    } else {
      // Turn on all field selection
      setSelected(['*']);
      setNotSelected([...selected, ...notSelected]);
    }

    setAllFieldsSelected((allFieldsSelected) => !allFieldsSelected);
  };

  const handleSaveNewFieldMap = async () => {
    setIsSaving(true);
    const newFieldMapItem = Object.assign({ isSelected: false, notSelected: [], selected: [] });

    if (newFieldMapItem) {
      newFieldMapItem.isSelected = true;
      newFieldMapItem.selected = selected;
      newFieldMapItem.notSelected = notSelected;
      newFieldMapItem.isUpdated = true;
    } else {
      throw new Error(`Cannot find stream in fieldMap. Current stream: ${stream}. Fieldmap state: ${JSON.stringify(newFieldMapItem)}. Current store: ${JSON.stringify(store.getState())}`);
    }
    setFieldMap(isFlowV2 ? entity.id : entity.tap, newFieldMapItem, stream, isFlowV2);
    setFieldMapSelection(newFieldMapItem, stream, isFlowV2);
    setIsSaving(false);
    UIActions.editFieldMap({ ...entity, entityType: isFlowV2 ? 'connector' : 'source' });
  };

  useEffect(() => {
    const fieldMap = toFieldMap(entity.catalog, fieldMapItem, stream, flow, filter);
    if (fieldMap[stream].selected) {
      setSelected(fieldMap[stream].selected);
    }

    if (fieldMap[stream].notSelected) {
      setNotSelected(fieldMap[stream].notSelected);
    }
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box display="flex" flexDirection="row" my={2} alignItems="center" gap={2}>
        <Tooltip title="Back to Field Map" placement="top">
          <IconButton onClick={() => backToFieldMapSelector(isFlowV2)}>
            <ChevronLeft color="inherit" sx={{ fontSize: '16px' }} />{' '}
          </IconButton>
        </Tooltip>
        <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
          <Typography variant="body1" fontWeight={500}>
            Field Editor ({stream})
          </Typography>
          <Typography variant="body2" color="text.lightish" fontWeight={300}>
            Choose relevant fields for import.
          </Typography>
        </Box>
        <Box display="flex" width="100%" justifyContent={'flex-end'}>
          <Button
            sx={{fontSize: '0.825rem', px: 2, py: 0.5 }}
            color="text"
            variant="outlined"
            size="small"
            onClick={() => toggleImportAll()}
          >
            {allFieldsSelected ? (
              <>
                {' '}
                <CloseIcon sx={{ fontSize: '16px', mr: 0.5 }} /> Select Specific Fields{' '}
              </>
            ) : (
              <>
                {' '}
                <CheckIcon sx={{ fontSize: '16px', mr: 0.5 }} /> Import All Fields
              </>
            )}
          </Button>
        </Box>
      </Box>
      {allFieldsSelected && (
        <Alert
          severity="success"
          icon={<InfoOutlined fontSize="inherit" sx={{ color: 'success.main' }} />}
          sx={{ border: '1px solid success.main', marginBottom: 2 }}
        >
          All fields are selected and ready to import.
        </Alert>
      )}
      <TransferList
        selected={selected || []}
        setSelected={setSelected}
        notSelected={notSelected || []}
        setNotSelected={setNotSelected}
        allFieldsSelected={allFieldsSelected}
        primaryKeys={primaryKeys}
      />
      <Box
        sx={{
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          color="text"
          sx={{ px: 4, opacity: 0.75 }}
          disabled={isSaving}
          variant="outlined"
          onClick={() => backToFieldMapSelector(isFlowV2)}
        >
          Back
        </Button>
        <Button color="primary" sx={{ px: 4 }} disabled={isSaving} variant="contained" onClick={handleSaveNewFieldMap}>
          {allFieldsSelected ? 'Import All Fields' : 'Confirm'}
        </Button>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state, props) => {
  const isFlowV2 = !!state.entities.selectedEntity?.id;
  const id = state.connectors.selectedConnector?.id;
  const tap = state.sources.selectedSource?.tap;
  const stream = isFlowV2 ? state.connectors.selectedStream : state.sources.selectedStream;
  const primaryKeys = isFlowV2 ? state.connectors.primaryKeys : state.sources.primaryKeys;

  return {
    entityId: isFlowV2 ? id : tap,
    stream,
    fieldMapItem: isFlowV2 ? state.connectors.fieldMapSelection?.[stream] : state.sources.fieldMapSelection?.[stream],
    fieldMaps: isFlowV2 ? state.connectors?.linked?.[id]?.fieldMap : state.sources.linked?.[tap]?.fieldMap,
    entity: isFlowV2 ? state.connectors?.linked?.[id] : state.sources?.linked?.[tap],
    filter: state.configuration.filter,
    flow: state.flows.selectedFlow,
    primaryKeys: primaryKeys?.[stream],
    isFlowV2,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    backToFieldMapSelector: (isFlowV2) => UIActions.backToFieldMapSelector(isFlowV2),
    setFieldMap: (tap, fieldMapItem, stream, isFlowV2) => UIActions.setFieldMap(tap, fieldMapItem, stream, isFlowV2),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(FieldMapList);
